import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { AlertTriangle, LifeBuoy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

/**
 * BackupRoleNoticeModal - Iter-133x (P0)
 *
 * Blocking acknowledgment modal shown whenever a talent is in a
 * `backup` assignment slot and has not yet agreed to the backup role
 * terms for the current notice version. Talent cannot dismiss the
 * modal without explicitly checking the agreement box + submitting.
 *
 * Usage — driven by whichever page mounts it:
 *   <BackupRoleNoticeModal
 *     bookingId={bookingId}
 *     onAcknowledged={() => refresh()}
 *     onClose={() => setOpen(false)}
 *   />
 *
 * Copy + version come from GET /api/talent/backup-notice so the modal
 * always renders the EXACT wording that will be persisted in the audit
 * row (version column). We never hand-roll the text on the frontend.
 */
const BackupRoleNoticeModal = ({ bookingId, open = true, mode = 'on_site', onAcknowledged, onClose }) => {
  const [notice, setNotice] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    // PO5 revised — pass mode so the notice body matches the shift's
    // backup configuration (on_site vs on_call).
    api.get('/talent/backup-notice', { params: { mode } })
      .then((res) => { if (alive) setNotice(res.data); })
      .catch((err) => { if (alive) toast.error(err?.response?.data?.detail || 'Failed to load backup notice'); });
    return () => { alive = false; };
  }, [open, mode]);

  const submit = async () => {
    if (!agreed || !notice?.version) return;
    setSubmitting(true);
    try {
      await api.post(`/talent/bookings/${bookingId}/acknowledge-backup`, {
        version: notice.version,
        agreed: true,
      });
      toast.success('Backup role acknowledgment recorded.');
      onAcknowledged?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record acknowledgment');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      // BLOCKING modal — user cannot dismiss without acting. Ignore
      // Radix's "close on overlay click" by only honoring explicit
      // programmatic close after a successful submit.
      onOpenChange={() => { /* intentionally ignored — must ack to close */ }}
    >
      <DialogContent
        data-testid="backup-role-notice-modal"
        // Hide the default close-X.
        className="max-w-lg [&>button[aria-label='Close']]:hidden"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-800">
            <LifeBuoy className="w-5 h-5 text-amber-600" />
            {notice?.title || 'Backup Role Notice'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Backup role acknowledgment required before this booking is active.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm leading-relaxed text-gray-800">
          {!notice ? (
            <div className="py-6 text-center text-gray-500">Loading notice…</div>
          ) : (
            <>
              <div className="flex gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>This is a mandatory acknowledgment. You cannot clock in until you agree.</span>
              </div>
              <div className="space-y-3" data-testid="backup-notice-body">
                {notice.body_paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <label className="flex items-start gap-3 p-3 rounded-md bg-gray-50 border border-gray-200 cursor-pointer"
                     data-testid="backup-ack-checkbox-label">
                <Checkbox
                  checked={agreed}
                  onCheckedChange={(v) => setAgreed(!!v)}
                  data-testid="backup-ack-checkbox"
                  className="mt-0.5"
                />
                <span className="text-sm text-gray-800 font-medium">
                  {notice.checkbox_label}
                </span>
              </label>
              <div className="text-[11px] text-gray-400 text-right">
                Notice version: {notice.version}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between pt-2">
          {onClose && (
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={submitting}
              className="text-gray-500"
              data-testid="backup-ack-dismiss-btn"
            >
              I'll do this later
            </Button>
          )}
          <Button
            onClick={submit}
            disabled={!agreed || submitting || !notice}
            className="bg-amber-600 hover:bg-amber-700 text-white font-semibold ml-auto"
            data-testid="backup-ack-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            I agree - continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BackupRoleNoticeModal;
